// src/homePage.jsx
import React, { useEffect, useState } from "react";
import { calculateEmiOptions } from "./App.jsx";
import { usePDF } from "react-to-pdf";

const RETAILERS_API_URL = "https://usethecred.com/api/v1retailer.php";
const CREATE_LOAN_API_URL = "https://usethecred.com/api/v1create_loan.php";

/* ------------ Toast (popup error) ------------ */
function Toast({ message }) {
  if (!message) return null;

  return (
    <div className="toast">
      <span className="toast-text">{message}</span>
    </div>
  );
}

/* ------------ Home page ------------ */
export function HomePage() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRetailer, setSelectedRetailer] = useState(null);

  useEffect(() => {
    async function fetchRetailers() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(RETAILERS_API_URL);
        const json = await res.json();

        if (!res.ok || !json.success) {
          throw new Error(json.message || "Failed to fetch retailers");
        }

        setRetailers(json.data || []);
      } catch (err) {
        console.error(err);
        setError("Unable to load retailers.");
      } finally {
        setLoading(false);
      }
    }

    fetchRetailers();
  }, []);

  const handleCardClick = (retailer) => {
    setSelectedRetailer(retailer);
  };

  const handleCloseForm = () => {
    setSelectedRetailer(null);
  };

  return (
    <>
      <div className="page page-home">
        <h2 className="page-title">Retailers</h2>

        {loading && <p className="text-muted">Loading retailers...</p>}
        {error && <p className="text-error">{error}</p>}

        {!loading && !error && retailers.length === 0 && (
          <p className="text-muted">No retailers found.</p>
        )}

        <div className="card-list">
          {retailers.map((retailer) => (
            <button
              key={retailer.id}
              className="card retailer-card retailer-card-button"
              onClick={() => handleCardClick(retailer)}
            >
              <div className="card-main">
                <div className="card-title">{retailer.retailer_name}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedRetailer && (
        <CustomerFormSheet retailer={selectedRetailer} onClose={handleCloseForm} />
      )}
    </>
  );
}

/* ---------------- Multi-step sheet + steps ---------------- */
const TOTAL_STEPS = 5;

function CustomerFormSheet({ retailer, onClose }) {
  const [step, setStep] = React.useState(1);
  const [selectedPlan, setSelectedPlan] = React.useState(null);
  const [error, setError] = React.useState("");
  const [isConfirmed, setIsConfirmed] = React.useState(false);
  const [createdLoanId, setCreatedLoanId] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [form, setForm] = React.useState({
    // Step 1 - Customer
    customerPhoto: null,
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerDob: "",
    customerMonthlySalary: "",
    customerAddress: "",
    // Step 2 - KYC
    aadharNumber: "",
    aadharFront: null,
    aadharBack: null,
    panNumber: "",
    panPhoto: null,
    accountNumber: "",
    ifscCode: "",
    // Step 3 - Item
    itemName: "",
    itemValue: "",
    itemImei: "",
    itemPhoto: null,
    // Step 4 - Downpayment & EMI
    downPayment: "",
  });

  // Lock body scroll while sheet open
  React.useEffect(() => {
    document.body.classList.add("body-locked");
    return () => {
      document.body.classList.remove("body-locked");
    };
  }, []);

  // Auto-hide error toast after 3 seconds
  React.useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  const handleTextChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      let newValue = value;

      // Input-level normalizations / filters
      if (name === "aadharNumber") {
        newValue = value.replace(/\D/g, "").slice(0, 12);
      }

      if (name === "panNumber") {
        newValue = value.toUpperCase();
      }

      const next = { ...prev, [name]: newValue };

      // if (name === "itemValue") {
      //   const num = Number(newValue) || 0;
      //   const minDp = getMinimumDownPayment(num);
      //   const currentDp = Number(prev.downPayment) || 0;

      //   if (!currentDp && minDp > 0) {
      //     next.downPayment = String(minDp);
      //   } else if (currentDp < minDp) {
      //     next.downPayment = String(minDp);
      //   }
      // }

      // if (name === "downPayment") {
      //   const itemNum = Number(prev.itemValue) || 0;
      //   const minDp = getMinimumDownPayment(itemNum);
      //   const entered = Number(newValue) || 0;
      //   if (entered < minDp) {
      //     next.downPayment = String(minDp);
      //   }
      if (name === "itemValue") {
        const num = Number(newValue) || 0;
        const dp = num > 0 ? Math.ceil(num * 0.2) : 0;
        next.downPayment = dp ? String(dp) : "";
      }
      
      if (name === "downPayment") {
        const itemNum = Number(prev.itemValue) || 0;
        const autoDp = itemNum > 0 ? Math.ceil(itemNum * 0.2) : 0;
        next.downPayment = autoDp ? String(autoDp) : "";
      }
      

      return next;
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files && files[0] ? files[0] : null;
    setForm((prev) => ({ ...prev, [name]: file }));
  };

  const progressPercent = (step / TOTAL_STEPS) * 100;

  // ---- Validations ----
  const validateStep1 = () => {
    if (!form.customerName.trim()) return "Customer name is required.";
    if (!form.customerPhone.trim()) return "Phone number is required.";
    if (!form.customerDob) return "Date of birth is required.";
    if (!form.customerMonthlySalary) return "Monthly salary is required.";
    if (!form.customerAddress.trim()) return "Address is required.";

    // Age validation: 21 to 50 years
    const today = new Date();
    const dob = new Date(form.customerDob);
    if (isNaN(dob.getTime())) {
      return "Enter a valid date of birth.";
    }

    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    if (age < 21 || age > 50) {
      return "Customer age must be between 21 and 50 years.";
    }

    return "";
  };

  const validateStep2 = () => {
    if (!form.aadharNumber.trim()) return "Aadhaar number is required.";
    if (!/^\d{12}$/.test(form.aadharNumber)) {
      return "Aadhaar number must be exactly 12 digits.";
    }
    if (!form.panNumber.trim()) return "PAN number is required.";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber)) {
      return "Enter a valid PAN (5 letters, 4 digits, 1 letter, all caps).";
    }
    if (!form.accountNumber.trim()) return "Account number is required.";
    if (!form.ifscCode.trim()) return "IFSC code is required.";
    return "";
  };

  const validateStep3 = () => {
    if (!form.itemName.trim()) return "Item name is required.";
    if (!form.itemValue || Number(form.itemValue) <= 0)
      return "Item value must be greater than 0.";
    return "";
  };

  const validateStep4 = () => {
    const itemValue = Number(form.itemValue) || 0;
    const downPayment = Number(form.downPayment) || 0;

    if (itemValue <= 0) {
      return "Enter a valid item value.";
    }

    // const minDp = getMinimumDownPayment(itemValue);
    // if (downPayment < minDp) {
    //   return `Downpayment must be at least ₹${minDp.toFixed(0)}.`;
    // }

    // const disbursement = itemValue - downPayment;
    // if (disbursement > 27000) {
    //   return "Disbursement amount cannot exceed ₹27,000.";
    // }

    const flatDp = Math.ceil(itemValue * 0.2);
if (downPayment !== flatDp) {
  return `Downpayment is fixed at ${flatDp.toFixed(0)} (20% of item value).`;
}
const disbursement = itemValue - downPayment;
// keep or remove this cap as per your business rule
if (disbursement > 27000) {
  return "Disbursement amount cannot exceed 27,000.";
}


    if (!selectedPlan) {
      return "Select an EMI plan to continue.";
    }

    return "";
  };

  const handleNext = () => {
    if (createdLoanId) return;

    let msg = "";
    if (step === 1) msg = validateStep1();
    if (step === 2) msg = validateStep2();
    if (step === 3) msg = validateStep3();
    if (step === 4) msg = validateStep4();

    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const handleBack = () => {
    if (createdLoanId) return;
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  // ---- EMI options for Step 4 ----
  const itemValueNum = Number(form.itemValue) || 0;
  const downPaymentNum = Number(form.downPayment) || 0;

  const emiOptions =
    step >= 4 && itemValueNum > 0 && downPaymentNum > 0
      ? calculateEmiOptions(itemValueNum, downPaymentNum)
      : [];

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    if (createdLoanId) return;

    const step4Error = validateStep4();
    if (step4Error) {
      setError(step4Error);
      return;
    }
    if (!isConfirmed) {
      setError("You must accept the loan terms to create the loan.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      const payload = { retailer, form, selectedPlan, emiOptions };

      const res = await fetch(CREATE_LOAN_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      console.log("Create loan raw response:", res.status, text);

      let json;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error("Invalid JSON from server");
      }

      if (!res.ok || !json.success) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }

      setCreatedLoanId(json.loan_id);
    } catch (err) {
      console.error("Create loan error:", err);
      setError("Unable to create loan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sheet-backdrop">
      <div className="sheet-container">
        <header className="sheet-header">
          <button
            className="sheet-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ✕
          </button>
          <div className="sheet-header-text">
            <div className="sheet-title">
              {createdLoanId ? "Loan Created" : "New Loan Application"}
            </div>
            <div className="sheet-subtitle">
              Retailer: {retailer.retailer_name}
            </div>
          </div>
        </header>

        {!createdLoanId && (
          <>
            <div className="sheet-progress-wrap">
              <div className="step-progress-bar">
                <div
                  className="step-progress-bar-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="step-progress-text">
                Step {step} of {TOTAL_STEPS}
              </div>
            </div>

            <div className="sheet-body">
              {step === 1 && (
                <StepCustomerDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 2 && (
                <StepKycDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 3 && (
                <StepItemDetails
                  form={form}
                  onTextChange={handleTextChange}
                  onFileChange={handleFileChange}
                />
              )}

              {step === 4 && (
                <StepDownpaymentEmi
                  form={form}
                  onTextChange={handleTextChange}
                  emiOptions={emiOptions}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                />
              )}

              {step === 5 && (
                <StepPreview
                  retailer={retailer}
                  form={form}
                  selectedPlan={selectedPlan}
                  isConfirmed={isConfirmed}
                  setIsConfirmed={setIsConfirmed}
                />
              )}
            </div>

            <div className="sheet-footer sheet-footer--wizard">
              <button
                type="button"
                className="btn btn-secondary sheet-footer-btn"
                onClick={step === 1 ? onClose : handleBack}
                disabled={isSubmitting}
              >
                {step === 1 ? "Close" : "Back"}
              </button>
              {step < TOTAL_STEPS && (
                <button
                  type="button"
                  className="btn btn-primary sheet-footer-btn"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </button>
              )}
              {step === TOTAL_STEPS && (
                <button
                  type="submit"
                  className="btn btn-primary sheet-footer-btn"
                  onClick={handleCreateLoan}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Loan"}
                </button>
              )}
            </div>
          </>
        )}

        {createdLoanId && (
          <SuccessView
            loanId={createdLoanId}
            onClose={onClose}
            retailer={retailer}
            form={form}
            selectedPlan={selectedPlan}
          />
        )}

        <Toast message={error} />
      </div>
    </div>
  );
}

/* ---- Success view after loan creation ---- */
function SuccessView({ loanId, onClose, retailer, form, selectedPlan }) {
  const { toPDF, targetRef } = usePDF({
    filename: `Credit_Agreement_${loanId}.pdf`,
  });

  const safeForm = form || {};
  const safeRetailer = retailer || {};
  const safePlan = selectedPlan || null;

  // Base numbers
  const itemValueNum = Number(safeForm.itemValue) || 0;
  const downPaymentNum = Number(safeForm.downPayment) || 0;
  const disbursementAmount = itemValueNum - downPaymentNum;

  // Company details
  const COMPANY_NAME = "UTC Finance Pvt. Ltd.";
  const COMPANY_ADDRESS =
    "Plot 47, Sagar Enclave, Rajendra Park, Gurgaon, Sadar Bazar, Gurgaon-122001";
  const COMPANY_PHONE = "+91-7413897932";
  const COMPANY_EMAIL = "hello@usethecred.com";
  const GRIEVANCE_OFFICER = "Sachin Singh";
  const GRIEVANCE_PHONE = "+91-7412813171";

  // Charges: GST removed
  const appLockFee = 200;
  const processingFeeAmount = safePlan?.processingFee || 0;
  const totalCharges = processingFeeAmount + appLockFee;

  // Loan Amount for agreement = disbursement + processing + app lock
  const loanAmountForAgreement =
    disbursementAmount + processingFeeAmount + appLockFee;

  // Generate EMI schedule
  const emiSchedule = [];
  if (safePlan) {
    const today = new Date();
    const months = safePlan.months;
    let remainingBalance = safePlan.totalRepay;

    for (let i = 0; i < months; i++) {
      const emiDate = new Date(today);
      emiDate.setMonth(emiDate.getMonth() + i + 1);
      remainingBalance = Math.max(0, remainingBalance - safePlan.emi);

      emiSchedule.push({
        emiNumber: i + 1,
        dueDate: emiDate.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        amount: safePlan.emi,
        remainingBalance: remainingBalance.toFixed(2),
      });
    }
  }

  const handleDownloadPdf = () => {
    toPDF();
  };

  return (
    <>
      {/* Visible success UI */}
      <div className="sheet-body">
        <section className="section-block">
          <h3 className="section-title">Congratulations!</h3>

          <div className="preview-block">
            <div className="preview-heading">
              Credit Agreement Created Successfully
            </div>
            <div className="preview-line">
              Your agreement ID is <strong>{loanId}</strong>.
            </div>
          </div>

          <div className="button-row">
            <button className="btn btn-primary" onClick={handleDownloadPdf}>
              Download Agreement
            </button>
          </div>

          <button
            className="btn btn-secondary"
            style={{ marginTop: 8 }}
            onClick={onClose}
          >
            Close
          </button>
        </section>
      </div>

      {/* Hidden multi-page credit agreement for PDF */}
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "210mm",
          height: "auto",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          backgroundColor: "#ffffff",
          color: "#111827",
          lineHeight: 1.5,
        }}
        ref={targetRef}
      >
        {/* PAGE 1: HEADER + CUSTOMER DETAILS (3-COLUMN LAYOUT) */}
        <div
          style={{
            width: "210mm",
            height: "297mm",
            padding: "10mm",
            boxSizing: "border-box",
            pageBreakAfter: "always",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "2px solid #1e40af",
              paddingBottom: "12px",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: "20px",
                  margin: 0,
                  fontWeight: "bold",
                }}
              >
                Mobile Purchase Credit Agreement
              </h1>
              <p
                style={{
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                  color: "#6b7280",
                }}
              >
                Agreement ID: {loanId}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                Page 1 of 5
              </div>
            </div>
          </div>

          {/* NACH PAYMENT NOTICE */}
          <div
            style={{
              backgroundColor: "#dbeafe",
              border: "2px solid #0284c7",
              borderRadius: "8px",
              padding: "12px",
              marginBottom: "16px",
              fontSize: "11px",
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: "#0284c7" }}>⚠️ NACH PAYMENT NOTICE:</strong>
            <p style={{ margin: "6px 0 0 0" }}>
              All EMI payments will be collected via{" "}
              <strong>NACH (National Automated Clearing House)</strong> on the
              due date. You must maintain sufficient balance in your registered
              bank account to avoid EMI bounce charges and penalties. Failure to
              maintain balance may result in late payment charges and device
              lock.
            </p>
          </div>

          {/* CUSTOMER DETAILS - 3 COLUMN LAYOUT */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            {/* Column 1: Customer Photo */}
            <div
              style={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                  padding: "6px 8px",
                  margin: "0 -12px 12px -12px",
                  width: "calc(100% + 24px)",
                  borderRadius: "6px 6px 0 0",
                  textAlign: "center",
                }}
              >
                CUSTOMER PHOTO
              </h3>

              {safeForm.customerPhoto ? (
                <div
                  style={{
                    display: "block",
                    width: "120px",
                    height: "140px",
                    border: "2px solid #d1d5db",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <img
                    src={URL.createObjectURL(safeForm.customerPhoto)}
                    alt="Customer Photo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "120px",
                    height: "140px",
                    border: "2px dashed #d1d5db",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#9ca3af",
                    fontSize: "11px",
                  }}
                >
                  No Photo
                </div>
              )}

              <p
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginTop: "8px",
                  textAlign: "center",
                }}
              >
                {safeForm.customerName || "Customer"}
              </p>
            </div>

            {/* Column 2: Personal Details */}
            <div
              style={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                  padding: "6px 8px",
                  margin: "0 -12px 12px -12px",
                  width: "calc(100% + 24px)",
                  borderRadius: "6px 6px 0 0",
                }}
              >
                PERSONAL DETAILS
              </h3>

              <div
                style={{
                  display: "grid",
                  gap: "6px",
                  fontSize: "10px",
                }}
              >
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Name
                  </div>
                  <div>{safeForm.customerName || "-"}</div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Phone
                  </div>
                  <div>{safeForm.customerPhone || "-"}</div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Email
                  </div>
                  <div style={{ fontSize: "9px" }}>
                    {safeForm.customerEmail || "-"}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    DOB
                  </div>
                  <div>{safeForm.customerDob || "-"}</div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Salary
                  </div>
                  <div>
                    ₹
                    {Number(
                      safeForm.customerMonthlySalary || 0
                    ).toLocaleString("en-IN")}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Address
                  </div>
                  <div
                    style={{ fontSize: "9px", lineHeight: 1.4 }}
                  >
                    {safeForm.customerAddress || "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: KYC & Device Details */}
            <div
              style={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                  padding: "6px 8px",
                  margin: "0 -12px 12px -12px",
                  width: "calc(100% + 24px)",
                  borderRadius: "6px 6px 0 0",
                }}
              >
                KYC & DEVICE
              </h3>

              <div
                style={{
                  display: "grid",
                  gap: "6px",
                  fontSize: "10px",
                }}
              >
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#10b981" }}>
                    Aadhaar
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {safeForm.aadharNumber || "Not Provided"}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#10b981" }}>
                    PAN
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {safeForm.panNumber || "Not Provided"}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#10b981" }}>
                    Account No.
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {safeForm.accountNumber || "Not Provided"}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#10b981" }}>
                    IFSC Code
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                    }}
                  >
                    {safeForm.ifscCode || "Not Provided"}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Device Name
                  </div>
                  <div>{safeForm.itemName || "-"}</div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Device Value
                  </div>
                  <div>
                    ₹
                    {Number(safeForm.itemValue || 0).toLocaleString(
                      "en-IN"
                    )}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Loan Amount
                  </div>
                  <div>
                    ₹
                    {loanAmountForAgreement.toLocaleString("en-IN")}
                  </div>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    paddingBottom: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    IMEI/Serial
                  </div>
                  <div style={{ fontSize: "9px" }}>
                    {safeForm.itemImei || "-"}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: "bold", color: "#1e40af" }}>
                    Retailer
                  </div>
                  <div>{safeRetailer.retailer_name || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Device Photo - Full Width Below */}
          {safeForm.itemPhoto && (
            <div
              style={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "12px",
                textAlign: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                  padding: "6px 8px",
                  margin: "0 -12px 12px -12px",
                  width: "calc(100% + 24px)",
                  borderRadius: "6px 6px 0 0",
                }}
              >
                DEVICE PHOTO
              </h3>
              <div
                style={{
                  display: "inline-block",
                  width: "150px",
                  height: "150px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#f9fafb",
                }}
              >
                <img
                  src={URL.createObjectURL(safeForm.itemPhoto)}
                  alt="Device Photo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* PAGE 2: CREDIT TERMS + CHARGES BREAKDOWN */}
        <div
          style={{
            width: "210mm",
            height: "297mm",
            padding: "10mm",
            boxSizing: "border-box",
            pageBreakAfter: "always",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              borderBottom: "2px solid #1e40af",
              paddingBottom: "12px",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: "20px",
                  margin: 0,
                  fontWeight: "bold",
                }}
              >
                Mobile Purchase Credit Agreement
              </h1>
              <p
                style={{
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                  color: "#6b7280",
                }}
              >
                Agreement ID: {loanId}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                }}
              >
                Page 2 of 5
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              flex: 1,
            }}
          >
            {/* Left: Credit Terms */}
            <div
              style={{
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                  padding: "6px 10px",
                  margin: "0 -12px 12px -12px",
                  borderRadius: "6px 6px 0 0",
                }}
              >
                CREDIT TERMS
              </h3>

              <div
                style={{
                  display: "grid",
                  gap: "6px",
                  fontSize: "11px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Agreement ID</span>
                  <span>{loanId}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Customer Name</span>
                  <span>{safeForm.customerName || "-"}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Device Value</span>
                  <span>
                    ₹
                    {Number(
                      safeForm.itemValue || 0
                    ).toLocaleString("en-IN")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Loan Amount</span>
                  <span>
                    ₹
                    {loanAmountForAgreement.toLocaleString("en-IN")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Down Payment</span>
                  <span>
                    ₹
                    {downPaymentNum.toLocaleString("en-IN")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Disbursement Amount</span>
                  <span>
                    ₹
                    {disbursementAmount.toLocaleString("en-IN")}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Tenure</span>
                  <span>
                    {safePlan ? safePlan.months : 0} months
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #d1d5db",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Interest Rate</span>
                  <span>0.065% per day</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "2px solid #1e40af",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Monthly EMI</span>
                  <span
                    style={{
                      fontWeight: "bold",
                      color: "#1e40af",
                      fontSize: "12px",
                    }}
                  >
                    ₹
                    {safePlan?.emi?.toLocaleString("en-IN", {
                      maximumFractionDigits: 0,
                    }) || "0"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "6px 0",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Agreement Date</span>
                  <span>{new Date().toLocaleDateString("en-IN")}</span>
                </div>
              </div>
            </div>

            {/* Right: Charges Breakdown */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Processing & Charges */}
              <div
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#1e40af",
                    color: "#ffffff",
                    padding: "6px 10px",
                    margin: "0 -12px 12px -12px",
                    borderRadius: "6px 6px 0 0",
                  }}
                >
                  CHARGES & FEES BREAKDOWN
                </h3>

                <div
                  style={{
                    display: "grid",
                    gap: "6px",
                    fontSize: "11px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #d1d5db",
                      padding: "6px 0",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      Processing Fee
                    </span>
                    <span>
                      ₹
                      {processingFeeAmount.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #d1d5db",
                      padding: "6px 0",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>App Lock Fee</span>
                    <span>
                      ₹{appLockFee.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderBottom: "2px solid #1e40af",
                      padding: "6px 0",
                      fontWeight: "bold",
                      color: "#1e40af",
                    }}
                  >
                    <span>Total Charges</span>
                    <span>
                      ₹
                      {totalCharges.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Late Payment Charges */}
              <div
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#dc2626",
                    color: "#ffffff",
                    padding: "6px 10px",
                    margin: "0 -12px 12px -12px",
                    borderRadius: "6px 6px 0 0",
                  }}
                >
                  LATE PAYMENT CHARGES
                </h3>

                <div
                  style={{
                    fontSize: "11px",
                    lineHeight: 1.6,
                  }}
                >
                  <p style={{ margin: "0 0 6px 0" }}>
                    <strong>
                      If payment is delayed beyond the due date:
                    </strong>
                  </p>
                  <ul
                    style={{
                      margin: "0 0 6px 0",
                      paddingLeft: "16px",
                    }}
                  >
                    <li>
                      1-5 days late: ₹100 per day or 2% of EMI, whichever is
                      higher
                    </li>
                    <li>
                      6-30 days late: ₹200 per day or 5% of EMI, whichever is
                      higher
                    </li>
                    <li>
                      30+ days late: Device will be locked and repossession
                      proceedings commence
                    </li>
                  </ul>
                </div>
              </div>

              {/* Prepayment */}
              <div
                style={{
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    backgroundColor: "#059669",
                    color: "#ffffff",
                    padding: "6px 10px",
                    margin: "0 -12px 12px -12px",
                    borderRadius: "6px 6px 0 0",
                  }}
                >
                  PREPAYMENT TERMS
                </h3>

                <p
                  style={{
                    fontSize: "11px",
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  You may prepay the remaining balance at any time{" "}
                  <strong>
                    without any prepayment penalty or charges
                  </strong>
                  . Contact {COMPANY_PHONE} for prepayment requests.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PAGE 3: EMI SCHEDULE */}
        <div style={{
          width: "210mm",
          height: "297mm",
          padding: "10mm",
          boxSizing: "border-box",
          pageBreakAfter: "always",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "2px solid #1e40af",
            paddingBottom: "12px",
          }}>
            <div>
              <h1 style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: "bold",
              }}>
                Mobile Purchase Credit Agreement
              </h1>
              <p style={{
                fontSize: "12px",
                margin: "4px 0 0 0",
                color: "#6b7280",
              }}>
                Agreement ID: {loanId}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "4px",
              }}>
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div style={{
                fontSize: "12px",
                color: "#6b7280",
              }}>
                Page 3 of 5
              </div>
            </div>
          </div>

          <h2 style={{
            fontSize: "16px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "#1e40af",
          }}>
            MONTHLY PAYMENT SCHEDULE
          </h2>

          <div style={{
            backgroundColor: "#f3f4f6",
            borderRadius: "8px",
            padding: "0",
            overflow: "hidden",
            flex: 1,
          }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
            }}>
              <thead>
                <tr style={{
                  backgroundColor: "#1e40af",
                  color: "#ffffff",
                }}>
                  <th style={{
                    padding: "8px 6px",
                    fontWeight: "bold",
                    textAlign: "left",
                    width: "15%",
                    borderRight: "1px solid #ddd",
                  }}>EMI No.</th>
                  <th style={{
                    padding: "8px 6px",
                    fontWeight: "bold",
                    textAlign: "left",
                    width: "25%",
                    borderRight: "1px solid #ddd",
                  }}>Due Date</th>
                  <th style={{
                    padding: "8px 6px",
                    fontWeight: "bold",
                    textAlign: "right",
                    width: "25%",
                    borderRight: "1px solid #ddd",
                  }}>Amount</th>
                  <th style={{
                    padding: "8px 6px",
                    fontWeight: "bold",
                    textAlign: "right",
                    width: "35%",
                  }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {emiSchedule.map((row, idx) => (
                  <tr key={idx} style={{
                    backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}>
                    <td style={{
                      padding: "6px",
                      borderRight: "1px solid #e5e7eb",
                    }}>{row.emiNumber}</td>
                    <td style={{
                      padding: "6px",
                      borderRight: "1px solid #e5e7eb",
                    }}>{row.dueDate}</td>
                    <td style={{
                      padding: "6px",
                      textAlign: "right",
                      borderRight: "1px solid #e5e7eb",
                      fontWeight: "500",
                    }}>₹{row.amount.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                    <td style={{
                      padding: "6px",
                      textAlign: "right",
                      fontSize: "9px",
                      color: "#6b7280",
                    }}>₹{Number(row.remainingBalance).toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "6px",
            fontSize: "11px",
            lineHeight: 1.6,
          }}>
            <strong>Payment Instructions:</strong>
            <ul style={{ margin: "6px 0 0 0", paddingLeft: "16px" }}>
              {/* <li>Pay by the due date to avoid late payment charges</li> */}
              <li>Payment via NACH will be deducted automatically</li>
              <li>Maintain sufficient balance to avoid bounce charges</li>
              <li>Receipt will be issued for every payment</li>
            </ul>
          </div>

          <div style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#dbeafe",
            border: "1px solid #0284c7",
            borderRadius: "6px",
            fontSize: "10px",
            lineHeight: 1.5,
          }}>
            <strong>Total Amount Payable:</strong> ₹{emiSchedule.reduce((sum, row) => sum + row.amount, 0).toLocaleString('en-IN', {maximumFractionDigits: 0})} over {emiSchedule.length} months
          </div>
        </div>

        {/* PAGE 4: DEVICE LOCK & POLICIES */}
        <div style={{
          width: "210mm",
          height: "297mm",
          padding: "10mm",
          boxSizing: "border-box",
          pageBreakAfter: "always",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "2px solid #1e40af",
            paddingBottom: "12px",
          }}>
            <div>
              <h1 style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: "bold",
              }}>
                Mobile Purchase Credit Agreement
              </h1>
              <p style={{
                fontSize: "12px",
                margin: "4px 0 0 0",
                color: "#6b7280",
              }}>
                Agreement ID: {loanId}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "4px",
              }}>
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div style={{
                fontSize: "12px",
                color: "#6b7280",
              }}>
                Page 4 of 5
              </div>
            </div>
          </div>

          <h2 style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "#dc2626",
          }}>
            DEVICE LOCK POLICY
          </h2>

          <div style={{
            backgroundColor: "#fee2e2",
            border: "2px solid #dc2626",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "16px",
            fontSize: "11px",
            lineHeight: 1.6,
          }}>
            <p style={{ margin: "0 0 8px 0", fontWeight: "bold" }}>
              ⚠️ DEVICE LOCK & EMI BOUNCE POLICY
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              If your EMI payment <strong>bounces or is delayed beyond 5 days</strong> from the due date, {COMPANY_NAME} has the right to:
            </p>
            <ul style={{ margin: "0 0 8px 0", paddingLeft: "16px" }}>
              <li><strong>Lock your device remotely</strong> to prevent use</li>
              <li><strong>Impose bounce charge</strong> of ₹500 per bounce</li>
              <li><strong>Charge late payment penalties</strong> as per late payment charges section</li>
              <li><strong>Proceed with device repossession</strong> after 30 days of default</li>
            </ul>
            <p style={{ margin: "0" }}>
              The device will be unlocked only after <strong>full payment of dues including bounce charges and late fees</strong>.
            </p>
          </div>

          <h2 style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "#1e40af",
          }}>
            TERMS & CONDITIONS
          </h2>

          <div style={{
            fontSize: "10px",
            lineHeight: 1.6,
            color: "#374151",
            flex: 1,
            overflowY: "auto",
          }}>
            <h4 style={{ marginTop: "0", marginBottom: "6px", fontWeight: "bold" }}>1. DEFAULT & REPOSSESSION</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              If you fail to pay any EMI for more than 30 days after the due date, {COMPANY_NAME} has the right to repossess the mobile device without notice. The device will be sold to recover the outstanding amount and related costs. Any shortfall will be recovered from you as per law.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>2. LATE PAYMENT CHARGES</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              Payments made after the due date will be charged as follows: (1) 10% of outstanding EMI will be charged as late payment fee. (2) Phone will be locked as per Device lock policy immediately after 1 DPD. (3) After 30 days, device repossession proceedings will commence.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>3. DEVICE OWNERSHIP & SECURITY</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              Until the full credit amount and all charges are paid, {COMPANY_NAME} retains a security interest in the mobile device. You agree to maintain the device in good condition and not sell, transfer, pledge, or encumber it. You will indemnify {COMPANY_NAME} against any loss or damage to the device.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>4. PREPAYMENT</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              You may prepay the outstanding balance at any time without penalty or prepayment charges. Contact {COMPANY_PHONE} for prepayment processing.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>5. NACH DEDUCTION & BANK BALANCE</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>All EMI payments will be collected via NACH (National Automated Clearing House)</strong> on the agreed due date from your designated bank account. You <strong>must maintain sufficient balance</strong> in your account to avoid EMI bounces, which will attract additional charges and penalties. EMI bounces may result in device lock and recovery action.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>6. GRIEVANCE REDRESSAL</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              For any queries or complaints: Contact {GRIEVANCE_OFFICER} at {GRIEVANCE_PHONE} or email {COMPANY_EMAIL}. Complaints will be resolved within 7 business days.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>7. DATA PRIVACY & CONFIDENTIALITY</h4>
            <p style={{ margin: "0 0 8px 0" }}>
              {COMPANY_NAME} will keep your personal and financial information confidential and will not share it with third parties except as required by law or for credit reporting to credit bureaus.
            </p>

            <h4 style={{ marginBottom: "6px", fontWeight: "bold" }}>8. GOVERNING LAW & JURISDICTION</h4>
            <p style={{ margin: "0" }}>
              This agreement is governed by the laws of India. Any disputes will be settled in the courts of Haryana, India (or your local jurisdiction).
            </p>
          </div>
        </div>

        {/* PAGE 5: BORROWER ACKNOWLEDGMENT & COMPANY DETAILS */}
        <div style={{
          width: "210mm",
          height: "297mm",
          padding: "10mm",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "2px solid #1e40af",
            paddingBottom: "12px",
          }}>
            <div>
              <h1 style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: "bold",
              }}>
                Mobile Purchase Credit Agreement
              </h1>
              <p style={{
                fontSize: "12px",
                margin: "4px 0 0 0",
                color: "#6b7280",
              }}>
                Agreement ID: {loanId}
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "4px",
              }}>
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <div style={{
                fontSize: "12px",
                color: "#6b7280",
              }}>
                Page 5 of 5
              </div>
            </div>
          </div>

          <h2 style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "#1e40af",
          }}>
            BORROWER'S ACKNOWLEDGMENT & CONSENT
          </h2>

          <div style={{
            backgroundColor: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "16px",
            fontSize: "11px",
            lineHeight: 1.6,
          }}>
            <p style={{ margin: "0" }}>
              I/We, <strong>{safeForm.customerName}</strong>, hereby acknowledge that I/we have read, understood, and agree to all terms, conditions, charges, penalties, and policies outlined in this Mobile Purchase Credit Agreement, including:
            </p>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "16px" }}>
              <li>Monthly EMI amount of ₹{safePlan?.emi?.toLocaleString('en-IN', {maximumFractionDigits: 0}) || "0"}</li>
              <li>All applicable charges and fees totaling ₹{totalCharges.toLocaleString('en-IN', {maximumFractionDigits: 0})}</li>
              <li>Late payment charges and penalties for missed or delayed payments</li>
              <li>Device lock policy in case of EMI bounce or default</li>
              <li>NACH automatic deduction from my bank account</li>
              <li>Device repossession rights if payment is overdue by 30+ days</li>
            </ul>
            <p style={{ margin: "8px 0 0 0" }}>
              I/We confirm that I/we have received a copy of this agreement and understand my/our obligations under this credit arrangement.
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "16px",
            flex: 1,
          }}>
            {/* Borrower Signature */}
            <div style={{
              borderTop: "2px solid #1e40af",
              paddingTop: "12px",
            }}>
              <p style={{ margin: "0 0 4px 0", fontWeight: "bold", fontSize: "11px" }}>
                Borrower's Signature / Initials
              </p>
              <p style={{ margin: "0 0 4px 0", fontSize: "10px", color: "#6b7280" }}>
                Name: {safeForm.customerName || "_______________________"}
              </p>
              <p style={{ margin: "0", fontSize: "10px", color: "#6b7280" }}>
                Date: {new Date().toLocaleDateString('en-IN')}
              </p>
            </div>

            {/* Lender Signature */}
            <div style={{
              borderTop: "2px solid #1e40af",
              paddingTop: "12px",
            }}>
              <p style={{ margin: "0 0 4px 0", fontWeight: "bold", fontSize: "11px" }}>
                Lender's Representative Signature
              </p>
              <p style={{ margin: "0 0 4px 0", fontSize: "10px", color: "#6b7280" }}>
                Name & Seal of {COMPANY_NAME}
              </p>
              <p style={{ margin: "0", fontSize: "10px", color: "#6b7280" }}>
                Date: {new Date().toLocaleDateString('en-IN')}
              </p>
            </div>
          </div>

          {/* Company Details */}
          <div style={{
            padding: "16px",
            backgroundColor: "#f3f4f6",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "10px",
            lineHeight: 1.6,
          }}>
            <h4 style={{ margin: "0 0 8px 0", fontWeight: "bold", fontSize: "11px" }}>
              {COMPANY_NAME}
            </h4>
            <p style={{ margin: "0 0 4px 0" }}>
              <strong>Address:</strong> {COMPANY_ADDRESS}
            </p>
            <p style={{ margin: "0 0 4px 0" }}>
              <strong>Phone:</strong> {COMPANY_PHONE}
            </p>
            <p style={{ margin: "0 0 4px 0" }}>
              <strong>Email:</strong> {COMPANY_EMAIL}
            </p>
            <p style={{ margin: "0" }}>
              <strong>Grievance Officer:</strong> {GRIEVANCE_OFFICER} ({GRIEVANCE_PHONE})
            </p>
          </div>
        </div>
      </div>

      {/* Toast popup for validation/API errors */}
      <Toast message="" />
    </>
  );
}

/* ---- Step 1: Customer Details ---- */
function StepCustomerDetails({ form, onTextChange, onFileChange }) {
  const handleRemoveFile = (field) => {
    onFileChange({ target: { name: field, files: [] } });
  };
  return (
    <section className="section-block">
      <h3 className="section-title">Customer Details</h3>

      <div className="form-group">
        <label className="field-label" htmlFor="customerPhoto">
          Customer Photo
        </label>
        <input
          id="customerPhoto"
          name="customerPhoto"
          type="file"
          accept="image/*"
          capture="user"
          className="field-input"
          onChange={onFileChange}
        />
        {form.customerPhoto && (
          <div className="file-preview">
            <img
              src={URL.createObjectURL(form.customerPhoto)}
              alt="Customer"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                marginTop: 8,
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => handleRemoveFile("customerPhoto")}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerName">
          Customer Name
        </label>
        <input
          id="customerName"
          name="customerName"
          type="text"
          className="field-input"
          placeholder="Enter customer full name"
          value={form.customerName}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerPhone">
          Phone Number
        </label>
        <input
          id="customerPhone"
          name="customerPhone"
          type="tel"
          className="field-input"
          placeholder="10-digit mobile number"
          value={form.customerPhone}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerEmail">
          Email ID <span className="field-optional">(optional)</span>
        </label>
        <input
          id="customerEmail"
          name="customerEmail"
          type="email"
          className="field-input"
          placeholder="customer@email.com"
          value={form.customerEmail}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerDob">
          Date of Birth
        </label>
        <input
          id="customerDob"
          name="customerDob"
          type="date"
          className="field-input"
          value={form.customerDob}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerMonthlySalary">
          Monthly Salary (₹)
        </label>
        <input
          id="customerMonthlySalary"
          name="customerMonthlySalary"
          type="number"
          className="field-input"
          placeholder="e.g. 25000"
          value={form.customerMonthlySalary}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="customerAddress">
          Address
        </label>
        <textarea
          id="customerAddress"
          name="customerAddress"
          className="field-input field-textarea"
          placeholder="House no, street, area, city"
          value={form.customerAddress}
          onChange={onTextChange}
          required
        />
      </div>
    </section>
  );
}

/* ---- Step 2: KYC Details ---- */
function StepKycDetails({ form, onTextChange, onFileChange }) {
  const handleRemoveFile = (field) => {
    onFileChange({ target: { name: field, files: [] } });
  };
  return (
    <section className="section-block">
      <h3 className="section-title">KYC Details</h3>

      <div className="form-group">
        <label className="field-label" htmlFor="aadharNumber">
          Aadhaar Number
        </label>
        <input
          id="aadharNumber"
          name="aadharNumber"
          type="text"
          className="field-input"
          placeholder="12-digit Aadhaar"
          value={form.aadharNumber}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="aadharFront">
          Aadhaar Photo (Front)
        </label>
        <input
          id="aadharFront"
          name="aadharFront"
          type="file"
          accept="image/*"
          capture="environment"
          className="field-input"
          onChange={onFileChange}
        />
        {form.aadharFront && (
          <div className="file-preview">
            <img
              src={URL.createObjectURL(form.aadharFront)}
              alt="Aadhaar front"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                marginTop: 8,
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => handleRemoveFile("aadharFront")}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="aadharBack">
          Aadhaar Photo (Back)
        </label>
        <input
          id="aadharBack"
          name="aadharBack"
          type="file"
          accept="image/*"
          capture="environment"
          className="field-input"
          onChange={onFileChange}
        />
        {form.aadharBack && (
          <div className="file-preview">
            <img
              src={URL.createObjectURL(form.aadharBack)}
              alt="Aadhaar back"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                marginTop: 8,
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => handleRemoveFile("aadharBack")}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="panNumber">
          PAN Number
        </label>
        <input
          id="panNumber"
          name="panNumber"
          type="text"
          className="field-input"
          placeholder="PAN"
          value={form.panNumber}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="panPhoto">
          PAN Photo
        </label>
        <input
          id="panPhoto"
          name="panPhoto"
          type="file"
          accept="image/*"
          capture="environment"
          className="field-input"
          onChange={onFileChange}
        />
        {form.panPhoto && (
          <div className="file-preview">
            <img
              src={URL.createObjectURL(form.panPhoto)}
              alt="PAN"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                marginTop: 8,
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => handleRemoveFile("panPhoto")}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="accountNumber">
          Bank Account Number
        </label>
        <input
          id="accountNumber"
          name="accountNumber"
          type="text"
          className="field-input"
          placeholder="Enter your account number"
          value={form.accountNumber}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="ifscCode">
          IFSC Code
        </label>
        <input
          id="ifscCode"
          name="ifscCode"
          type="text"
          className="field-input"
          placeholder="e.g. SBIN0012345"
          value={form.ifscCode}
          onChange={onTextChange}
          required
        />
      </div>
    </section>
  );
}

/* ---- Step 3: Item Details ---- */
function StepItemDetails({ form, onTextChange, onFileChange }) {
  const handleRemoveFile = (field) => {
    onFileChange({ target: { name: field, files: [] } });
  };
  return (
    <section className="section-block">
      <h3 className="section-title">Item Details</h3>

      <div className="form-group">
        <label className="field-label" htmlFor="itemName">
          Item Name
        </label>
        <input
          id="itemName"
          name="itemName"
          type="text"
          className="field-input"
          placeholder="e.g. iPhone 15, vivo v25 etc."
          value={form.itemName}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="itemValue">
          Item Value (₹)
        </label>
        <input
          id="itemValue"
          name="itemValue"
          type="number"
          className="field-input"
          placeholder="e.g. 60000"
          value={form.itemValue}
          onChange={onTextChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="itemImei">
          IMEI Number
        </label>
        <input
          id="itemImei"
          name="itemImei"
          type="text"
          className="field-input"
          placeholder="IMEI (if applicable)"
          value={form.itemImei}
          onChange={onTextChange}
        />
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="itemPhoto">
          Photo With Device
        </label>
        <input
          id="itemPhoto"
          name="itemPhoto"
          type="file"
          accept="image/*"
          capture="environment"
          className="field-input"
          onChange={onFileChange}
        />
        {form.itemPhoto && (
          <div className="file-preview">
            <img
              src={URL.createObjectURL(form.itemPhoto)}
              alt="Device"
              style={{
                width: 80,
                height: 80,
                objectFit: "cover",
                marginTop: 8,
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 8 }}
              onClick={() => handleRemoveFile("itemPhoto")}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---- Step 4: Downpayment & EMI selection ---- */
function StepDownpaymentEmi({
  form,
  onTextChange,
  emiOptions,
  selectedPlan,
  setSelectedPlan,
}) {
  // const itemValue = Number(form.itemValue) || 0;
  // const minDp = getMinimumDownPayment(itemValue);
  const itemValue = Number(form.itemValue) || 0;
const flatDp = itemValue > 0 ? Math.ceil(itemValue * 0.2) : 0;

  return (
    <section className="section-block">
      <h3 className="section-title">Downpayment & EMI</h3>

      <div className="form-group">
        <label className="field-label">Item Value</label>
        <div className="field-input field-input--static">
          ₹{itemValue.toFixed(0)}
        </div>
      </div>

      <div className="form-group">
        <label className="field-label" htmlFor="downPayment">
          Downpayment (₹)
        </label>
        <input
  id="downPayment"
  name="downPayment"
  type="number"
  className="field-input"
  placeholder="20% of item value"
  value={form.downPayment}
  onChange={onTextChange}
  readOnly
/>
<div className="text-note">
  Downpayment is fixed at 20% of item value.
</div>
      </div>

      {emiOptions.length > 0 && (
        <div className="card-list card-list--emi">
          {emiOptions.map((opt) => {
            const isActive = selectedPlan && selectedPlan.key === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                className={
                  "card emi-card emi-card-selectable" +
                  (isActive ? " emi-card--active" : "")
                }
                onClick={() => setSelectedPlan(opt)}
              >
                <div className="card-header-row">
                  <div className="emi-title">
                    {opt.label} • {opt.months} month
                    {opt.months > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="emi-row">
                  <span className="emi-label">Loan Amount</span>
                  <span className="emi-value">
                    ₹{opt.loanAmount.toFixed(0)}
                  </span>
                </div>
                <div className="emi-row">
                  <span className="emi-label">Net Disbursement</span>
                  <span className="emi-value">
                    ₹{opt.netDisbursement.toFixed(0)}
                  </span>
                </div>
                <div className="emi-row">
                  <span className="emi-label">Last EMI Date</span>
                  <span className="emi-value">{opt.lastRepaymentDate}</span>
                </div>
                <div className="emi-row emi-row--highlight">
                  <span className="emi-label">Monthly EMI</span>
                  <span className="emi-value emi-value--primary">
                    ₹{opt.emi.toFixed(0)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ---- Step 5: Preview ---- */
function StepPreview({
  retailer,
  form,
  selectedPlan,
  isConfirmed,
  setIsConfirmed,
}) {
  return (
    <section className="section-block">
      <h3 className="section-title">Loan Preview</h3>

      <div className="preview-block">
        <div className="preview-heading">Retailer</div>
        <div className="preview-line">{retailer.retailer_name}</div>
      </div>

      <div className="preview-block">
        <div className="preview-heading">Customer Details</div>
        <div className="preview-line">
          <strong>Name:</strong> {form.customerName}
        </div>
        <div className="preview-line">
          <strong>Phone:</strong> {form.customerPhone}
        </div>
        {form.customerEmail && (
          <div className="preview-line">
            <strong>Email:</strong> {form.customerEmail}
          </div>
        )}
        <div className="preview-line">
          <strong>DOB:</strong> {form.customerDob}
        </div>
        <div className="preview-line">
          <strong>Monthly Salary:</strong> ₹
          {Number(form.customerMonthlySalary).toFixed(2)}
        </div>
        <div className="preview-line">
          <strong>Address:</strong> {form.customerAddress}
        </div>
      </div>

      <div className="preview-block">
        <div className="preview-heading">KYC Details</div>
        <div className="preview-line">
          <strong>Aadhaar:</strong> {form.aadharNumber}
        </div>
        <div className="preview-line">
          <strong>PAN:</strong> {form.panNumber}
        </div>
        <div className="preview-line">
          <strong>Account Number:</strong> {form.accountNumber}
        </div>
        <div className="preview-line">
          <strong>IFSC Code:</strong> {form.ifscCode}
        </div>
      </div>

      <div className="preview-block">
        <div className="preview-heading">Item Details</div>
        <div className="preview-line">
          <strong>Item:</strong> {form.itemName}
        </div>
        <div className="preview-line">
          <strong>Value:</strong> ₹{Number(form.itemValue).toFixed(2)}
        </div>
        {form.itemImei && (
          <div className="preview-line">
            <strong>IMEI:</strong> {form.itemImei}
          </div>
        )}
      </div>

      <div className="preview-block">
        <div className="preview-heading">Loan Amount Details</div>
        <div className="preview-line">
          <strong>Item Value:</strong> ₹{Number(form.itemValue).toFixed(2)}
        </div>
        <div className="preview-line">
          <strong>Down Payment:</strong> ₹
          {Number(form.downPayment).toFixed(2)}
        </div>
        <div className="preview-line">
          <strong>Disbursement:</strong> ₹
          {(Number(form.itemValue) - Number(form.downPayment)).toFixed(2)}
        </div>
      </div>

      {selectedPlan && (
        <div className="preview-block">
          <div className="preview-heading">EMI Plan</div>
          <div className="preview-line">
            <strong>Plan:</strong> {selectedPlan.label} •{" "}
            {selectedPlan.months} month
            {selectedPlan.months > 1 ? "s" : ""}
          </div>
          <div className="preview-line">
            <strong>Loan Amount:</strong> ₹
            {selectedPlan.loanAmount.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Processing Fee:</strong> ₹
            {selectedPlan.processingFee.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Interest Amount:</strong> ₹
            {selectedPlan.interest.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Net Disbursement:</strong> ₹
            {selectedPlan.netDisbursement.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Total Repay Amount:</strong> ₹
            {selectedPlan.totalRepay.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Monthly EMI:</strong> ₹{selectedPlan.emi.toFixed(0)}
          </div>
          <div className="preview-line">
            <strong>Last EMI Date:</strong> {selectedPlan.lastRepaymentDate}
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="field-label">
          <input
            type="checkbox"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
          />{" "}
          I confirm that the above information is correct and I accept the loan
          terms and conditions.
        </label>
        <div className="text-note">
          You must accept the terms before creating the loan.
        </div>
      </div>
    </section>
  );
}

export default HomePage;
